<template>
    <v-container id="default-accounts" fluid>
        <!-- top side -->
        <v-row dense align="center" class="mb-2">
			<v-col cols="auto">
				<slot name="breadcrumbs"></slot>
			</v-col>
        </v-row>

        <!-- table -->
        <bee-handy-table
            :headers="[
                { text: 'image', value: 'image', name: 'image', columnClass: 'px-0', width: 80 },
                { text: 'type', value: 'type', name: 'type', columnClass: 'px-1', width: 180 },
                { text: 'details', value: 'details', name: 'details', columnClass: 'px-2', divider: true }
            ]"
            :items="loading ? [] : defaultAccounts"
            :loading="loading"
            :items-per-page="-1"
            :data-table-props="{ disableSort: true }"
            hide-default-header
            hide-default-footer
        	dense
        >
            <!-- account image -->
            <template v-slot:item.image="{ item }">
                <div class="py-1">
                    <v-img
                        :src="item.img"
                        height="45"
                        width="45"
                        contain
                        transition="scale-transition"
                        class="mt-sm-0 mt-2 mx-auto"
                    />
                </div>
            </template>

            <!-- account type -->
            <template v-slot:item.type="{ item }">
                <div class="text-sm-start text-center primary--text">{{ item.name }}</div>
            </template>

            <!-- details -->
            <template v-slot:item.details="{ item, index }">
                <v-slide-y-transition mode="out-in">
                    <!-- data -->
                    <v-row
                    	v-if="!item.isEdit"
                    	:no-gutters="$vuetify.breakpoint.smAndUp"
                        dense
                        align="center"
                    	class="mb-sm-0 mb-2"
                    	key="data"
                    >
                        <!-- account name -->
                        <v-col
                        	class="text-sm-start text-center"
                        	:lg="$emerald.ifLang('es', '3', null)"
                        	md="4"
                        	sm="6"
                        	cols="12"
                        >
                            <span class="grey--text">{{$t('DefaultAccountsPage.name')}}:</span>
                            <span class="ms-1">{{item.value}}</span>
                        </v-col>
                        
                        <!-- account details -->
                        <v-col
                            v-for="(detail, i) in item.details"
                            :key="i"
                            class="text-sm-start text-center"
                            :lg="i === 0 ? 1 : 3" md="4" sm="6" cols="12"
                        >
                            <span class="grey--text">{{detail.name}}:</span>
                            <span class="ms-1">{{detail.value}}</span>
                        </v-col>
                        
                        <!-- actions -->
                        <v-spacer/>
                        <v-col
                        	v-if="!isFileLocked"
                        	sm="auto"
                        	cols="12"
                        	class="text-sm-start text-center"
                        >
                            <!-- edit -->
                            <tooltip :text="$t('buttons.edit')" top>
                                <v-btn
                                	height="34"
                                	width="38"
                                	min-width="38"
                                	color="edit-color white--text"
                                    :disabled="item.resetLoading || isViewer"
                                	@click="
                                        defaultAccounts.forEach((c, i) => {
                                            if (index !== i && defaultAccounts[i].isEdit)
                                                defaultAccounts[i].isEdit = false;
                                        });
                                        item.isEdit = true;
                                    "
                                >
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                            </tooltip>
                            
                            <!-- reset -->
                            <tooltip v-if="item.accountType" :text="$t('DefaultAccountsPage.reset')" top>
                                <v-btn
                                	height="34"
                                	width="38"
                                	min-width="38"
                                	color="danger-dark-color white--text"
                                	class="ms-2"
                                    :loading="item.resetLoading"
                                    :disabled="isViewer"
                                    @click="
                                        item.resetLoading = true;
                                        resetDefaultAccount(item.accountType).finally(() => {
                                            item.resetLoading = false;
                                        })
                                    "
                                >
                                    <v-icon>mdi-delete</v-icon>
                                </v-btn>
                            </tooltip>
                        </v-col>
                    </v-row>
                    
                    <!-- edit -->
                    <v-row
                    	v-else-if="!isFileLocked"
                    	dense
                    	align="center"
                    	class="ma-0"
                    	key="edit"
                    >
                        <!-- inputs -->
                        <v-col md="3" sm="4" cols="8">
                            <bee-account-autocomplete
                                v-if="!item.isWarehouse"
                                v-model="item.modelValue"
                                :label="item.inputLabel"
                                :items="item.inputItems"
                                :with-balance="true"
                                :hideDetails="true"
                                context="financial_file"
                                :action-name="actions.EDIT"
                            ></bee-account-autocomplete>

                            <bee-autocomplete
                                v-else-if="item.isWarehouse"
                                v-model="item.modelValue"
                                :items="warehouses"
                                item-text="name"
                                item-value="id"
                                :label="$t('DefaultAccountsPage.warehouse')"
                                :hideDetails="true"
                                context="warehouse"
                                :action-name="actions.EDIT"
                            ></bee-autocomplete>
                        </v-col>

                        <!-- actions -->
                        <v-col cols="auto">
                            <!-- save -->
                            <tooltip top :text="$t('buttons.save')">
                                <v-btn
                                    height="34"
                                    width="38"
                                    min-width="38"
                                    color="success-light-color white--text"
                                    class="ms-4"
                                    :loading="item.loading"
                                    @click="
                                        item.loading = true;
                                        (
                                            item.accountType
                                                ? changeDefaultAccount(item.modelValue, item.accountType)
                                                : item.updateHandler(item.modelValue)
                                        ).finally(() => {
                                            item.loading = false;
                                        })
                                    "
                                >
                                    <v-icon>mdi-check-circle-outline</v-icon>
                                </v-btn>
                            </tooltip>

                            <!-- cancel -->
                            <tooltip top :text="$t('buttons.cancel')">
                                <v-btn
                                    height="34"
                                    width="38"
                                    min-width="38"
                                    color="white"
                                    class="ms-2"
                                    :disabled="item.loading"
                                    @click="
                                        item.isEdit = false;
                                        item.modelValue = item.originalModelId;
                                    "
                                >
                                    <v-icon>mdi-close-circle-outline</v-icon>
                                </v-btn>
                            </tooltip>
                        </v-col>
                    </v-row>
                </v-slide-y-transition>
            </template>
        </bee-handy-table>  
    </v-container>
</template>
<script>
import { mapState, mapGetters } from "vuex";
import actions from "@/permissions/actions-names.js"
import { accountConfigType } from '@/helpers/enums';

export default {
    name: 'DefaultAccountsPage',
    data (){
        return{
        actions,
        loading: false,
        defaultAccounts: []
    }},

    computed:{
        ...mapState({
            defaultFinancialFileAccounts: state => state.configs.default_financial_file_accounts,
            configs: state => state.configs.configs,
            mainBox: state => state.accounts.main_box,
            warehouses: state => state.warehouses.warehouses,
        }),
        ...mapGetters({
			isViewer: 'auth/isViewer'
		}),

        isFileLocked() { return this.configs?.financial_file?.is_locked },
    },

    methods:{
        initializingDefaultAccounts() {
            this.defaultAccounts = [];
            const getAccountName = account => account && account.accounting_no ? account.accounting_no + '-' +  account.name : '-';
            const getType = type =>
                type
                    ? this.configs.account_types.map(c => ({
                        ...c,
                        name:
                            c.value == 1 ? this.$t('DefaultAccountsPage.type.main') :
                            c.value == 2 ? this.$t('DefaultAccountsPage.type.sub') :
                            c.name
                    })).find(c=> c.value === type).name
                    : '-'
            const getCategory = parentId => parentId ? this.configs.accounts.find(c => c.id === parentId).name: '-';
            const getFinalAccountType = finalType =>
                finalType
                    ? this.configs.final_account_types.map(c => ({
                        ...c,
                        name:
                            c.value == 1 ? this.$t('DefaultAccountsPage.final-account.operations'):
                            c.value == 2 ? this.$t('DefaultAccountsPage.final-account.trade'):
                            c.value == 3 ? this.$t('DefaultAccountsPage.final-account.gains-and-losses'):
                            c.value == 4 ? this.$t('DefaultAccountsPage.final-account.budget'):
                            c.name 
                        })).find(c => c.value == finalType).name
                    : '-'

            // warehouse
            const warehouse = this.configs.warehouse;
            this.defaultAccounts.push({
                isWarehouse: true,
                img: require('@/assets/store-grey.png'),
                name: this.$t('DefaultAccountsPage.default-warehouse'),
                value: warehouse && warehouse.name ? warehouse.name : '-',
                details: [
                    {
                        name: this.$t('DefaultAccountsPage.no'),
                        value: warehouse && warehouse.warehouse_no ? warehouse.warehouse_no  : '-'
                    },
                    {
                        name: this.$t('DefaultAccountsPage.notes'),
                        value: warehouse && warehouse.notes ? warehouse.notes : '-'
                    }                       
                ],
                
                originalModelId: warehouse?.id,
                modelValue: warehouse?.id,
                isEdit: false,

                loading: false,
                updateHandler: id => {
                    if (id) {
                        return this.$store.dispatch('warehouses/changeDefaultWarehouse', id).then(() => {
                            this.loading = true;
                            this.$store.dispatch('configs/fetchAll').then(() => {
                                this.initializingDefaultAccounts();
                            }).finally(() => {
                                this.loading = false;
                            })
                        })
                    } else {
                        return Promise.all([]);
                    }
                }
            });

            // main box
            this.defaultAccounts.push({
                img: require('@/assets/money-bag-grey.png'),
                name: this.$t('DefaultAccountsPage.fund'),
                value: getAccountName(this.mainBox),
                details: [
                    {
                        name: this.$t('DefaultAccountsPage.type.title'),
                        value: getType(this.mainBox?.type)
                    },
                    {
                        name: this.$t('DefaultAccountsPage.category'),
                        value: getCategory(this.mainBox?.parent_account_id)
                    },
                    {
                        name: this.$t('DefaultAccountsPage.final-account.title'),
                        value: getFinalAccountType(this.mainBox?.final_type)
                    },
                ],

                originalModelId: this.mainBox?.id,
                modelValue: this.mainBox?.id,
                inputLabel: this.$t('DefaultAccountsPage.account'),
                inputItems: this.configs.accounts,
                isEdit: false,

                loading: false,

                updateHandler: id => {
                    if (id) {
                        return this.$store.dispatch('accounts/changeMainBox', id).then(()=>{
                            this.loading = true;
                            this.$store.dispatch('accounts/getMainBox').then(() => {
                                this.initializingDefaultAccounts();
                            }).finally(() => {
                                this.loading = false;
                            })
                        })
                    } else {
                        return Promise.all([]);
                    }
                }
            });

            // accounts
            const mainAccountsData = [
                {
                    accountId: this.defaultFinancialFileAccounts.purchases_account_id,
                    img: require('@/assets/clerk.png'),
                    name: this.$t('DefaultAccountsPage.purchasing-account'),
                    inputLabel: this.$t('DefaultAccountsPage.account-trade'),
                    inputItems: this.configs.accounts.filter(c => c.type === 2 && c.final_type === 2) || [],
                    accountType: accountConfigType.purchases
                },
                {
                    accountId: this.defaultFinancialFileAccounts.purchases_refund_account_id,
                    img: require('@/assets/cashback.png'),
                    name: this.$t('DefaultAccountsPage.purchasing-refund-account'),
                    inputLabel: this.$t('DefaultAccountsPage.account-trade'),
                    inputItems: this.configs.accounts.filter(c => c.type === 2 && c.final_type === 2),
                    accountType:  accountConfigType.purchasesRefund
                },
                {
                    accountId: this.defaultFinancialFileAccounts.sales_account_id,
                    img: require('@/assets/saler.png'),
                    name: this.$t('DefaultAccountsPage.sales-account'),
                    inputLabel: this.$t('DefaultAccountsPage.account-trade'),
                    inputItems: this.configs.accounts.filter(c => c.type === 2 && c.final_type === 2),
                    accountType: accountConfigType.sales
                },
                {
                    accountId: this.defaultFinancialFileAccounts.sales_refund_account_id,
                    img: require('@/assets/box-back.png'),
                    name: this.$t('DefaultAccountsPage.sales-refund-account'),
                    inputLabel: this.$t('DefaultAccountsPage.account-trade'),
                    inputItems: this.configs.accounts.filter(c => c.type === 2 && c.final_type === 2),
                    accountType: accountConfigType.salesRefund
                },
                {
                    accountId: this.defaultFinancialFileAccounts.granted_discount_id,
                    img: require('@/assets/give-money.png'),
                    name: this.$t('DefaultAccountsPage.granted-discount-account'),
                    inputLabel: this.$t('DefaultAccountsPage.account-gains-and-losses-or-trade'),
                    inputItems: this.configs.accounts.filter(c => (c.final_type === 3 || c.final_type === 2) && c.type === 2),
                    accountType: accountConfigType.grantedDiscount
                },
                {
                    accountId: this.defaultFinancialFileAccounts.vested_discount_id,
                    img: require('@/assets/get-money.png'),
                    name: this.$t('DefaultAccountsPage.earned-discount-account'),
                    inputLabel: this.$t('DefaultAccountsPage.account-gains-and-losses-or-trade'),
                    inputItems: this.configs.accounts.filter(c => (c.final_type === 3 || c.final_type === 2) && c.type === 2),
                    accountType: accountConfigType.vestedDiscount
                },
                {
                    accountId: this.defaultFinancialFileAccounts.retained_earnings_id,
                    img: require('@/assets/profit-and-loss.png'),
                    name: this.$t('DefaultAccountsPage.rotate-profit-and-loss'),
                    inputLabel: this.$t('DefaultAccountsPage.account-budget'),
                    inputItems: this.configs.accounts.filter(c => c.type === 2 && c.final_type === 4),
                    accountType: accountConfigType.retainedEarnings
                },
                {
                    accountId: this.defaultFinancialFileAccounts.ending_inventory_id,
                    img: require('@/assets/materials.png'),
                    name: this.$t('DefaultAccountsPage.rotate-materials-end-of-term'),
                    inputLabel: this.$t('DefaultAccountsPage.account-budget'),
                    inputItems: this.configs.accounts.filter(c => c.type === 2 && c.final_type === 4),
                    accountType: accountConfigType.endingInventory
                },
            ];
            mainAccountsData.forEach(item => {
                const account = this.configs?.accounts?.find(c => c.id === item.accountId);
                this.defaultAccounts.push({
                    img: item.img,
                    name: item.name,
                    value: getAccountName(account),
                    details: [
                        {
                            name: this.$t('DefaultAccountsPage.type.title'),
                            value: getType(account?.type)
                        },
                        {
                            name: this.$t('DefaultAccountsPage.category'),
                            value: getCategory(account?.parent_account_id)
                        },
                        {
                            name: this.$t('DefaultAccountsPage.final-account.title'),
                            value: getFinalAccountType(account?.final_type)
                        },
                    ],

                    originalModelId: account?.id,
                    modelValue: account?.id,
                    inputLabel: item.inputLabel,
                    inputItems: item.inputItems,
                    accountType: item.accountType,
                    isEdit: false,

                    loading: false,
                    resetLoading: false,
                });
            })
        },

        resetDefaultAccount(accountType) {
            return this.$store.dispatch('configs/removeDefaultFinancialFileAccounts', {
                type: accountType
            }).then(() => {
                this.loading = true;
                this.$store.dispatch('configs/fetchDefaultFinancialFileAccounts').then(() => {
                    this.initializingDefaultAccounts();
                }).finally(() => {
                    this.loading = false;
                });
            })
        },

        changeDefaultAccount(id, type){
            if (id && type) {
                return this.$store.dispatch('configs/changeDefaultFinancialFileAccounts', {
                    accountConfigType: type,
                    accountId: id
                }).then(() => {
                    this.loading = true;
                    this.$store.dispatch('configs/fetchDefaultFinancialFileAccounts').then(() => {
                        this.initializingDefaultAccounts();
                    }).finally(() => {
                        this.loading = false;
                    })
                })
            } else {
                return Promise.all([]);
            }
        },
    },

    created() {
        this.loading = true;
        Promise.all([
            this.$store.dispatch('accounts/getMainBox'),
            this.$store.dispatch('warehouses/fetchAll'),
            this.$store.dispatch('configs/fetchDefaultFinancialFileAccounts'),
            Object.keys(this.configs).length === 0 ? this.$store.dispatch('configs/fetchAll') : null
        ]).then(() => {
            this.initializingDefaultAccounts()
        }).finally(() => {
            this.loading = false;
        });
    }
}
</script>

<style lang="scss">
#default-accounts {
    .v-data-table__mobile-row {
        justify-content: center;
    }
    td > .primary--text {
        border: none;
    }
    tr:not(.v-data-table__empty-wrapper) {
        & > td:nth-of-type(1), & > td:nth-of-type(2) {
            background: rgba(12, 58, 99, 0.12);
        }
    }
}
</style>